<template>
  <div class="portaria-panel">
    <div class="portaria-header">
      <h2 class="portaria-title">Portaria</h2>
      <p class="portaria-subtitle">Liberação e registro de entrada e saída de pessoas na obra.</p>
    </div>

    <div class="portaria-tabs">
      <button class="portaria-tab" :class="{ active: activeTab === 'portao' }" type="button" @click="activeTab = 'portao'">
        Portão
        <span v-if="insideCount > 0" class="portaria-tab-badge">{{ insideCount }}</span>
      </button>
      <button v-if="accessLevel >= 2" class="portaria-tab" :class="{ active: activeTab === 'liberacoes' }" type="button" @click="activeTab = 'liberacoes'">
        Liberações
        <span v-if="pendingReleases.length > 0" class="portaria-tab-badge">{{ pendingReleases.length }}</span>
      </button>
      <button v-if="accessLevel >= 3" class="portaria-tab" :class="{ active: activeTab === 'historico' }" type="button" @click="activeTab = 'historico'">Histórico</button>
    </div>

    <div class="portaria-body">
      <!-- ── Portão: quem está dentro + registrar entrada/saída ── -->
      <div v-if="activeTab === 'portao'" class="portaria-section">
        <div class="portaria-form">
          <div class="portaria-section-label">Registrar entrada</div>
          <div v-if="pendingReleases.length" class="portaria-field portaria-field-wide">
            <label class="portaria-label">Confirmar visitante esperado</label>
            <select class="portaria-select" v-model="selectedReleaseId" @change="applyRelease">
              <option value="">— Visitante não esperado —</option>
              <option v-for="r in pendingReleases" :key="r.id" :value="r.id">{{ r.name }} — {{ r.company || r.reason }}</option>
            </select>
          </div>
          <div class="portaria-grid">
            <div class="portaria-field">
              <label class="portaria-label">Nome</label>
              <input class="portaria-input" type="text" v-model="entryForm.name" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Documento</label>
              <input class="portaria-input" type="text" v-model="entryForm.document" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Empresa / Motivo</label>
              <input class="portaria-input" type="text" v-model="entryForm.company" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Responsável na obra</label>
              <select class="portaria-select" v-model="entryForm.hostUserId">
                <option value="">— Selecione —</option>
                <option v-for="u in userOptions" :key="u.username" :value="u.username">{{ u.name }}</option>
              </select>
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Veículo / Placa (opcional)</label>
              <input class="portaria-input" type="text" v-model="entryForm.vehiclePlate" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Obra (opcional)</label>
              <select class="portaria-select" v-model="entryForm.projectId">
                <option value="">— Não especificado —</option>
                <option v-for="p in projects" :key="p.id" :value="p.id">{{ p.name }}</option>
              </select>
            </div>
          </div>
          <p v-if="error" class="portaria-error">{{ error }}</p>
          <div class="portaria-actions">
            <button class="portaria-btn portaria-btn-primary" type="button" :disabled="saving" @click="submitEntry">
              {{ saving ? 'Registrando...' : 'Registrar entrada' }}
            </button>
          </div>
        </div>

        <div class="portaria-section-label" style="margin-top:20px">Dentro da obra agora</div>
        <div v-if="loadingEntries" class="portaria-loading">Carregando...</div>
        <div v-else-if="insideEntries.length === 0" class="portaria-empty">Ninguém registrado no momento.</div>
        <div v-else class="portaria-list">
          <div v-for="e in insideEntries" :key="e.id" class="portaria-item">
            <div class="portaria-item-main">
              <div class="portaria-item-title">{{ e.name }}</div>
              <div class="portaria-item-meta">
                {{ e.company || e.reason || '—' }}
                <span v-if="e.projectName"> · {{ e.projectName }}</span>
                <span> · entrou às {{ formatTime(e.entryAt) }}</span>
              </div>
            </div>
            <button class="portaria-btn portaria-btn-danger" type="button" @click="exit(e.id)">Registrar saída</button>
          </div>
        </div>
      </div>

      <!-- ── Liberações: pré-cadastro de visitante esperado ── -->
      <div v-else-if="activeTab === 'liberacoes'" class="portaria-section">
        <div class="portaria-form">
          <div class="portaria-section-label">Nova liberação prévia</div>
          <div class="portaria-grid">
            <div class="portaria-field">
              <label class="portaria-label">Nome</label>
              <input class="portaria-input" type="text" v-model="releaseForm.name" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Documento</label>
              <input class="portaria-input" type="text" v-model="releaseForm.document" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Empresa / Motivo</label>
              <input class="portaria-input" type="text" v-model="releaseForm.company" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Responsável na obra</label>
              <select class="portaria-select" v-model="releaseForm.hostUserId">
                <option value="">— Selecione —</option>
                <option v-for="u in userOptions" :key="u.username" :value="u.username">{{ u.name }}</option>
              </select>
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Veículo / Placa (opcional)</label>
              <input class="portaria-input" type="text" v-model="releaseForm.vehiclePlate" />
            </div>
            <div class="portaria-field">
              <label class="portaria-label">Previsto para</label>
              <input class="portaria-input" type="datetime-local" v-model="releaseForm.expectedAt" />
            </div>
          </div>
          <p v-if="releaseError" class="portaria-error">{{ releaseError }}</p>
          <div class="portaria-actions">
            <button class="portaria-btn portaria-btn-primary" type="button" :disabled="savingRelease" @click="submitRelease">
              {{ savingRelease ? 'Liberando...' : 'Liberar visitante' }}
            </button>
          </div>
        </div>

        <div class="portaria-section-label" style="margin-top:20px">Visitantes aguardados</div>
        <div v-if="pendingReleases.length === 0" class="portaria-empty">Nenhuma liberação pendente.</div>
        <div v-else class="portaria-list">
          <div v-for="r in pendingReleases" :key="r.id" class="portaria-item">
            <div class="portaria-item-main">
              <div class="portaria-item-title">{{ r.name }}</div>
              <div class="portaria-item-meta">
                {{ r.company || r.reason || '—' }}
                <span v-if="r.expectedAt"> · previsto {{ formatDateTime(r.expectedAt) }}</span>
              </div>
            </div>
            <button class="portaria-btn" type="button" @click="cancel(r.id)">Cancelar</button>
          </div>
        </div>
      </div>

      <!-- ── Histórico ── -->
      <div v-else-if="activeTab === 'historico'" class="portaria-section">
        <div class="portaria-filters">
          <div class="portaria-field">
            <label class="portaria-label">De</label>
            <input class="portaria-input" type="date" v-model="historyFilters.dateFrom" />
          </div>
          <div class="portaria-field">
            <label class="portaria-label">Até</label>
            <input class="portaria-input" type="date" v-model="historyFilters.dateTo" />
          </div>
          <div class="portaria-field portaria-field-actions">
            <button class="portaria-btn" type="button" :disabled="loadingHistory" @click="loadHistory">{{ loadingHistory ? 'Buscando...' : 'Buscar' }}</button>
            <button class="portaria-btn portaria-btn-primary" type="button" :disabled="loadingHistory || historyRows.length === 0" @click="exportHistory">Exportar Excel</button>
          </div>
        </div>
        <div v-if="loadingHistory" class="portaria-loading">Carregando...</div>
        <div v-else-if="historyRows.length === 0" class="portaria-empty">Nenhum registro encontrado.</div>
        <table v-else class="portaria-table">
          <thead>
            <tr>
              <th>Nome</th>
              <th>Empresa / Motivo</th>
              <th>Obra</th>
              <th>Entrada</th>
              <th>Saída</th>
              <th>Registrado por</th>
            </tr>
          </thead>
          <tbody>
            <tr v-for="r in historyRows" :key="r.id">
              <td>{{ r.name }}</td>
              <td>{{ r.company || r.reason || '—' }}</td>
              <td>{{ r.projectName || '—' }}</td>
              <td>{{ formatDateTime(r.entryAt) }}</td>
              <td>{{ r.exitAt ? formatDateTime(r.exitAt) : '—' }}</td>
              <td>{{ r.registeredByName }}</td>
            </tr>
          </tbody>
        </table>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { computed, defineComponent, onMounted, reactive, ref } from 'vue'
import * as XLSX from 'xlsx'
import { portariaStore, type PortariaExportRow } from '@/store/portaria'
import { accountsStore } from '@/store/accounts'
import { projectsStore } from '@/store/projects'

export default defineComponent({
  name: 'PortariaOverviewPanel',
  props: {
    accessLevel: { type: Number, default: 0 }
  },
  setup() {
    const activeTab = ref<'portao' | 'liberacoes' | 'historico'>('portao')
    const loadingEntries = ref(true)
    const saving = ref(false)
    const error = ref('')
    const selectedReleaseId = ref('')

    const savingRelease = ref(false)
    const releaseError = ref('')

    const loadingHistory = ref(false)
    const historyRows = ref<PortariaExportRow[]>([])
    const historyFilters = reactive({ dateFrom: '', dateTo: '' })

    const entryForm = reactive({
      releaseId: '',
      name: '',
      document: '',
      company: '',
      hostUserId: '',
      vehiclePlate: '',
      projectId: ''
    })

    const releaseForm = reactive({
      name: '',
      document: '',
      company: '',
      hostUserId: '',
      vehiclePlate: '',
      expectedAt: ''
    })

    const userOptions = computed(() =>
      accountsStore.all().filter(u => u.name).sort((a, b) => (a.name || '').localeCompare(b.name || ''))
    )
    const projects = computed(() => projectsStore.all())

    const insideEntries = computed(() => portariaStore.state.entries)
    const insideCount = computed(() => insideEntries.value.length)
    const pendingReleases = computed(() => portariaStore.state.releases)

    const formatTime = (iso: string) => iso ? new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' }) : ''
    const formatDateTime = (iso: string) => iso ? new Date(iso).toLocaleString('pt-BR') : ''

    const applyRelease = () => {
      const release = pendingReleases.value.find(r => r.id === selectedReleaseId.value)
      if (!release) return
      entryForm.releaseId = release.id
      entryForm.name = release.name
      entryForm.document = release.document
      entryForm.company = release.company
      entryForm.hostUserId = release.hostUserId
      entryForm.vehiclePlate = release.vehiclePlate
      entryForm.projectId = release.projectId
    }

    const resetEntryForm = () => {
      entryForm.releaseId = ''
      entryForm.name = ''
      entryForm.document = ''
      entryForm.company = ''
      entryForm.hostUserId = ''
      entryForm.vehiclePlate = ''
      entryForm.projectId = ''
      selectedReleaseId.value = ''
    }

    const submitEntry = async () => {
      error.value = ''
      if (!entryForm.name.trim()) {
        error.value = 'Informe o nome da pessoa.'
        return
      }
      saving.value = true
      try {
        const project = projects.value.find(p => p.id === entryForm.projectId)
        await portariaStore.registerEntry({
          releaseId: entryForm.releaseId,
          projectId: entryForm.projectId,
          projectName: project?.name || '',
          name: entryForm.name.trim(),
          document: entryForm.document.trim(),
          company: entryForm.company.trim(),
          hostUserId: entryForm.hostUserId,
          vehiclePlate: entryForm.vehiclePlate.trim()
        })
        resetEntryForm()
        await portariaStore.loadReleases('pending')
      } catch (err) {
        error.value = err instanceof Error ? err.message : 'Erro ao registrar entrada.'
      } finally {
        saving.value = false
      }
    }

    const exit = async (id: string) => {
      await portariaStore.registerExit(id)
    }

    const submitRelease = async () => {
      releaseError.value = ''
      if (!releaseForm.name.trim()) {
        releaseError.value = 'Informe o nome do visitante.'
        return
      }
      savingRelease.value = true
      try {
        await portariaStore.createRelease({
          name: releaseForm.name.trim(),
          document: releaseForm.document.trim(),
          company: releaseForm.company.trim(),
          hostUserId: releaseForm.hostUserId,
          vehiclePlate: releaseForm.vehiclePlate.trim(),
          expectedAt: releaseForm.expectedAt
        })
        releaseForm.name = ''
        releaseForm.document = ''
        releaseForm.company = ''
        releaseForm.hostUserId = ''
        releaseForm.vehiclePlate = ''
        releaseForm.expectedAt = ''
      } catch (err) {
        releaseError.value = err instanceof Error ? err.message : 'Erro ao liberar visitante.'
      } finally {
        savingRelease.value = false
      }
    }

    const cancel = async (id: string) => {
      await portariaStore.cancelRelease(id)
    }

    const loadHistory = async () => {
      loadingHistory.value = true
      try {
        historyRows.value = await portariaStore.fetchEntriesExport(historyFilters)
      } finally {
        loadingHistory.value = false
      }
    }

    const exportHistory = () => {
      const header = ['Nome', 'Documento', 'Empresa/Motivo', 'Obra', 'Responsável', 'Veículo', 'Entrada', 'Saída', 'Registrado por']
      const data = historyRows.value.map(r => [
        r.name, r.document, r.company || r.reason, r.projectName, r.hostName, r.vehiclePlate,
        formatDateTime(r.entryAt), r.exitAt ? formatDateTime(r.exitAt) : '', r.registeredByName
      ])
      const workbook = XLSX.utils.book_new()
      const sheet = XLSX.utils.aoa_to_sheet([header, ...data])
      sheet['!cols'] = [{ wch: 24 }, { wch: 16 }, { wch: 24 }, { wch: 22 }, { wch: 22 }, { wch: 14 }, { wch: 18 }, { wch: 18 }, { wch: 22 }]
      XLSX.utils.book_append_sheet(workbook, sheet, 'Portaria')
      const content = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' })
      const blob = new Blob([content], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const url = URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = 'portaria-historico.xlsx'
      a.click()
      setTimeout(() => URL.revokeObjectURL(url), 60000)
    }

    onMounted(async () => {
      try {
        if (accountsStore.all().length === 0) await accountsStore.refresh()
        if (projectsStore.all().length === 0) await projectsStore.refresh()
        await Promise.all([
          portariaStore.loadEntries({ status: 'inside' }),
          portariaStore.loadReleases('pending')
        ])
      } finally {
        loadingEntries.value = false
      }
    })

    return {
      activeTab, loadingEntries, saving, error, selectedReleaseId,
      savingRelease, releaseError, loadingHistory, historyRows, historyFilters,
      entryForm, releaseForm, userOptions, projects, insideEntries, insideCount, pendingReleases,
      formatTime, formatDateTime, applyRelease, submitEntry, exit, submitRelease, cancel, loadHistory, exportHistory
    }
  }
})
</script>

<style scoped lang="scss">
.portaria-panel {
  display: flex;
  flex-direction: column;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.portaria-header { padding: 18px 22px 0; display: flex; flex-direction: column; gap: 4px; }
.portaria-title { font-size: 20px; font-weight: 700; color: var(--ink); margin: 0; }
.portaria-subtitle { font-size: 13px; color: var(--ink-muted); margin: 0; }

.portaria-tabs {
  display: flex; gap: 4px; padding: 14px 22px 0;
  border-bottom: 1px solid rgba(31,30,26,.1);
}

.portaria-tab {
  display: flex; align-items: center; gap: 6px;
  padding: 8px 16px; border: none; background: none;
  font-size: 13px; font-weight: 600; color: var(--ink-muted); cursor: pointer;
  border-bottom: 2px solid transparent; margin-bottom: -1px;
  &.active { color: var(--accent); border-bottom-color: var(--accent); }
}

.portaria-tab-badge {
  display: inline-flex; align-items: center; justify-content: center;
  min-width: 18px; height: 18px; padding: 0 5px;
  background: var(--accent); color: #fff;
  border-radius: 999px; font-size: 11px; font-weight: 700;
}

.portaria-body { flex: 1; overflow-y: auto; padding: 20px 22px; }

.portaria-section { display: flex; flex-direction: column; gap: 8px; }

.portaria-section-label { font-size: 13px; font-weight: 700; color: var(--ink); }

.portaria-form, .portaria-filters {
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.portaria-filters { flex-direction: row; flex-wrap: wrap; align-items: flex-end; }

.portaria-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 12px;
}

.portaria-field { display: flex; flex-direction: column; gap: 4px; }
.portaria-field-wide { grid-column: 1 / -1; }
.portaria-field-actions { flex-direction: row; gap: 8px; margin-left: auto; }

.portaria-label { font-size: 12px; font-weight: 600; color: var(--ink-muted); }

.portaria-input, .portaria-select {
  border: 1px solid rgba(31,30,26,.18);
  border-radius: 8px;
  padding: 8px 10px;
  font-size: 13px;
  color: var(--ink);
  background: var(--bg, #fff);
  font-family: inherit;
}

.portaria-error { color: #dc2626; font-size: 12px; margin: 0; }

.portaria-actions { display: flex; justify-content: flex-end; }

.portaria-btn {
  border: none;
  border-radius: 8px;
  padding: 9px 18px;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  background: rgba(31,30,26,.08);
  color: var(--ink);
  &:disabled { opacity: .6; cursor: default; }
}
.portaria-btn-primary { background: var(--accent); color: #fff; }
.portaria-btn-danger { background: rgba(220,38,38,.1); color: #dc2626; }

.portaria-loading, .portaria-empty { font-size: 13px; color: var(--ink-muted); padding: 16px 0; }

.portaria-list { display: flex; flex-direction: column; gap: 8px; }

.portaria-item {
  display: flex; align-items: center; justify-content: space-between; gap: 12px;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 10px;
  padding: 10px 14px;
}

.portaria-item-title { font-size: 13px; font-weight: 700; color: var(--ink); }
.portaria-item-meta { font-size: 12px; color: var(--ink-muted); }

.portaria-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  background: var(--card, #fff);
  border: 1px solid rgba(31,30,26,.1);
  border-radius: 12px;
  overflow: hidden;

  th, td {
    text-align: left;
    padding: 9px 12px;
    border-bottom: 1px solid rgba(31,30,26,.08);
  }
  th { font-size: 11px; text-transform: uppercase; letter-spacing: .03em; color: var(--ink-muted); background: rgba(31,30,26,.03); }
  tbody tr:last-child td { border-bottom: none; }
}
</style>
